import {LitElement, html, css} from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import store from '../src/store';
import "./post-actions.js";
import "./post-save.js";
import { commentReaction } from '../src/apiActions';
import { getTimePassed, debounce } from '../src/apiCommon.js';
import { SldsBaseElement } from '../components/slds-base-element.js';

@customElement('single-comment')
class SingleComment extends connect(store)(SldsBaseElement) {
  @property({type: Object}) item = null;
  @property({type: Object}) likedLookup = {};
  @property({type: Object}) dislikedLookup = {};
  @property({type: Boolean}) isVisible = false;
  @property({type: Boolean}) fullyOutOfView = false;
  @property({type: Object}) statusLookup = {};
  @property({type: Object}) user = null;

  constructor() {
    super();
  
    this.observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          this.isVisible = true;
          this.fullyOutOfView = false;
        } else if (!this.fullyOutOfView) {
          this.fullyOutOfView = true;
          debounce(() => {
            if (this.fullyOutOfView) this.isVisible = false;
          }, 200);  // Adjust delay as needed
        }
      },
      { root: null, rootMargin: '800px', threshold: 0.5 }
    );
  }

  connectedCallback() {
    super.connectedCallback();
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer.unobserve(this);
    super.disconnectedCallback();
  }

  stateChanged(state) {
    this.accessToken = state.accessToken;
    this.user = state.user;
  }

  getLikes(comment) {
    var label = comment.upvotes > 1 ? `${comment.upvotes} ` : ``;
    return comment.upvote ? `${label}Liked` : `${label}Like`;
	}

	getDislikes(comment) {
		// if has more than 0 downvotes, return X disliked, otherwise return whether the current user disliked it along with the number of downvotes
		var label = comment.downvotes > 1 ? `${comment.downvotes} ` : ``;
		return comment.downvote ? `${label}Disliked` : `${label}Dislike`;
	}

  static styles = [
    super.styles,
    css`
		.slds-post__footer-action, .slds-text-heading--label-normal, .slds-text-heading_label-normal, .slds-text-title, .slds-text-color--weak, .slds-text-color_weak {
			color: var(--text-color);
		}
		.slds-post__footer-action.slds-is-active, .slds-post__footer-action:focus, .slds-post__footer-action:hover, .slds-text-link {
			color: #0176d3 !important;
		}
		img, video {
			object-fit: contain;
			max-height: 20rem;
			margin-left: auto;
			margin-right: auto;
		}
		.background {
			min-height: 20rem;
			display: inline-block;
		}
    @keyframes fadeIn {
      from {
        opacity: 0;
      }
      to {
        opacity: 1;
      }
    }
    .fade-in {
      opacity: 0;
      animation: fadeIn 0.3s ease forwards;
    }
    `
  ];

  getVideoOrImage(files) {
	if (files.light && !files.default) {
		files.default = files.light;
	}
    if (files && files.default) {
      const fileUrl = files.default.file_url;
      const ext = fileUrl.split(".").pop();
      if (ext === "webp") {
        return html`<a href="${fileUrl}" target="_blank"><img src="${fileUrl}"></a>`;
      }
      else if (ext === "webm") {
        return html`<video width="100%" autoplay="autoplay" muted="muted" loop>
          <source src="${fileUrl}" type="video/webm">
          Your browser does not support the video tag.
        </video>`;
      }
      // files.default
      // debugger;
    }
  }

  getVideoOrImageUploadedImage(uploadedImage) {
    if (uploadedImage && uploadedImage.fileUrl) {
      const fileUrl = uploadedImage.fileUrl;
      const ext = fileUrl.split(".").pop();
			const alt = uploadedImage.message;
      if (ext === "webp") {
        return html`<img src="${fileUrl}" alt="${alt}">`;
      }
      else if (ext === "webm") {
        return html`<video width="100%" controls>
          <source src="${fileUrl}" type="video/webm">
          Your browser does not support the video tag.
        </video>`;
      }
    }
  }

  toggleReplies(commentId) {
		this.dispatchEvent(new CustomEvent('toggle-replies', {detail: {commentId}}));
  }

	likeComment(comment, event) {
		if (comment.downvote) {
			comment.downvote = false;
			comment.downvotes--;
		}
		if (comment.upvote) {
			comment.upvote = false;
			comment.upvotes--;
		}
		else {
			comment.upvote = true;
			comment.upvotes++;
		}
		this.requestUpdate("likedLookup");
		store.dispatch(commentReaction(comment, 'upvote', comment.upvote));
		console.log("likeComment", comment, event);
	}

  getStatus(statusId) {
    return this.statusLookup[statusId] ? this.statusLookup[statusId].title.substring(0, 5) : '';
  }

	dislikeComment(comment, event) {
		if (comment.upvote) {
			comment.upvote = false;
			comment.upvotes--;
		}
		if (comment.downvote) {
			comment.downvote = false;
			comment.downvotes--;
		}
		else {
			comment.downvote = true;
			comment.downvotes++;
		}
		this.requestUpdate("dislikedLookup");
		store.dispatch(commentReaction(comment, 'downvote', comment.downvote));
		console.log("dislikeComment", comment, event);
	}

  render() {
    return html`
      ${!this.isVisible ? html`<div style="height: 5rem;"></div>` : html`
    <header class="slds-post__header slds-media fade-in">
        <div class="slds-media__figure slds-p-top_xx-small">
            <a href="/user/${this.item.username}" class="slds-avatar slds-avatar_circle slds-avatar_medium">
                <img alt="${this.item.posted_by.displayName}" src="${this.item.posted_by.photoURL}" title="${this.item.posted_by.displayName} avatar" />
            </a>
        </div>
        <div class="slds-media__body">
            <div class="slds-grid slds-grid_align-spread slds-has-flexi-truncate">
                <p>
                    <a href="/user/${this.item.username}" title="${this.item.posted_by.displayName}">${this.item.posted_by.displayName}</a>
                    ${this.item.posted_by.status ? html`<span title="${this.statusLookup[this.item.posted_by.status] ? this.statusLookup[this.item.posted_by.status].title : ''}" class="slds-text-color--weak">${this.getStatus(this.item.posted_by.status)}</span>` : ''}
                </p>
                <!-- <button class="slds-button slds-button_icon slds-button_icon-border slds-button_icon-x-small" aria-haspopup="true" title="More Options">
                    <svg class="slds-button__icon" aria-hidden="true">
                        <use xlink:href="/assets/icons/utility-sprite/svg/symbols.svg#down"></use>
                    </svg>
                    <span class="slds-assistive-text">More Options</span>
                </button> -->
            </div>
            <p class="slds-text-body_small">
                <a href="#" title="Click for single-item view of this post" class="slds-text-link_reset timeago">${getTimePassed(this.item.created)}</a>
            </p>
        </div>
    </header>
    <div class="slds-post__content slds-text-longform slds-m-bottom_x-small">
        <p>${this.item.comment_content}</p>
        ${this.item.files ? html`
            <div class="background slds-m-bottom_small">${this.getVideoOrImage(this.item.files)}</div>
        ` : ''}
    </div>
    <footer class="slds-post__footer">
        <ul class="slds-post__footer-actions-list slds-list_horizontal">
            <li class="slds-col slds-item slds-m-right_medium">
                <button @click="${(e) => this.likeComment(this.item, e)}" title="Like this item" class="${this.item.upvote ? 'slds-is-active ' : ''} slds-button_reset slds-post__footer-action" aria-pressed="false">
                    <svg class="slds-icon slds-icon-text-default slds-icon_x-small slds-align-middle" aria-hidden="true">
                        <use xlink:href="/assets/icons/utility-sprite/svg/symbols.svg#like"></use>
                    </svg>${this.getLikes(this.item)}</button>
            </li>
            <li class="slds-col slds-item slds-m-right_medium">
                <button @click="${(e) => this.dislikeComment(this.item, e)}" title="Dislike this item" class="${this.item.downvote ? 'slds-is-active ' : ''} slds-button_reset slds-post__footer-action" aria-pressed="false">
                    <svg class="slds-icon slds-icon-text-default slds-icon_x-small slds-align-middle" aria-hidden="true">
                        <use xlink:href="/assets/icons/utility-sprite/svg/symbols.svg#dislike"></use>
                    </svg>${this.getDislikes(this.item)}</button>
            </li>
            ${this.user ? html`<li class="slds-col slds-item slds-m-right_medium">
                <button @click="${() => this.toggleReplies(this.item.id)}" title="Comment on this item" class="slds-button_reset slds-post__footer-action">
                    <svg class="slds-icon slds-icon-text-default slds-icon_x-small slds-align-middle" aria-hidden="true">
                        <use xlink:href="/assets/icons/utility-sprite/svg/symbols.svg#share_post"></use>
                    </svg> Comment</button>
            </li>` : ''}
            <li class="slds-col slds-item slds-m-right_medium">
                <button title="Share this item" class="slds-button_reset slds-post__footer-action">
                    <svg class="slds-icon slds-icon-text-default slds-icon_x-small slds-align-middle" aria-hidden="true">
                        <use xlink:href="/assets/icons/utility-sprite/svg/symbols.svg#share"></use>
                    </svg> Share</button>
            </li>
        </ul>
        <ul class="slds-post__footer-meta-list slds-list_horizontal slds-has-dividers_right slds-text-title">
            <li class="slds-item">${this.item.comment_count} comments</li>
        </ul>
    </footer>
    `}
    `;
  }
}